// src/components/AddDevice.js
import React, { useState, useEffect } from 'react';
import { firestore } from '../firebase';
import { LockClosedIcon, LockOpenIcon } from '@heroicons/react/24/solid';

function AddDevice({ onClose, menuItems, currentDevice }) {
  const [deviceId, setDeviceId] = useState('');
  const [storeCode, setStoreCode] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [menuItemId, setMenuItemId] = useState('');
  const [screenPosition, setScreenPosition] = useState('');
  const [showAQI, setShowAQI] = useState(false);
  const [deviceIdUnlocked, setDeviceIdUnlocked] = useState(false);

  // Password for unlocking deviceId editing (frontend only)
  const EDIT_PASSWORD = 'admin123';

  // If we're editing, pre-fill the form with the current device's data
  useEffect(() => {
    if (currentDevice) {
      setDeviceId(currentDevice.deviceId);
      setStoreCode(currentDevice.storeCode || '');
      setDeviceName(currentDevice.deviceName);
      setMenuItemId(currentDevice.menuItemId || '');
      setScreenPosition(currentDevice.screenPosition || '');
      setShowAQI(currentDevice.showAQI ?? false);
    } else {
      setShowAQI(false);
    }
  }, [currentDevice]);

  const handleUnlockDeviceId = () => {
    const password = prompt('Enter password to edit Device ID:');
    if (password === EDIT_PASSWORD) {
      setDeviceIdUnlocked(true);
    } else if (password !== null) {
      alert('Incorrect password');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!deviceId || !deviceName) {
      alert('Please provide Device ID and Branch Name.');
      return;
    }

    const newDevice = {
      deviceId,
      deviceName,
      storeCode,
      menuItemId: menuItemId || null, // If no menu item is selected, leave it as null
      screenPosition: screenPosition || null, // If no screen position is selected, leave it as null
      showAQI,
    };

    if (currentDevice) {
      // Update existing device
      await firestore.collection('cafeTVs').doc(currentDevice.id).update(newDevice);
    } else {
      // Add new device
      await firestore.collection('cafeTVs').add(newDevice);
    }

    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-center">
      <div className="bg-white p-6 rounded shadow-lg w-full max-w-lg">
        <h3 className="text-xl font-bold mb-4">{currentDevice ? 'Edit Device' : 'Add Device'}</h3>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block mb-1">Device ID</label>
            <div className="relative flex items-center gap-2">
              <input
                type="text"
                disabled={currentDevice && !deviceIdUnlocked}
                className="flex-1 border px-3 py-2 rounded disabled:bg-gray-100 disabled:cursor-not-allowed"
                value={deviceId}
                onChange={(e) => setDeviceId(e.target.value)}
                required
              />
              {currentDevice && (
                <button
                  type="button"
                  onClick={handleUnlockDeviceId}
                  className="text-gray-600 hover:text-teal-500 transition-colors"
                  title={deviceIdUnlocked ? "Device ID unlocked for editing" : "Click to unlock Device ID editing"}
                >
                  {deviceIdUnlocked ? (
                    <LockOpenIcon className="h-6 w-6 text-teal-500" />
                  ) : (
                    <LockClosedIcon className="h-6 w-6" />
                  )}
                </button>
              )}
            </div>
          </div>
          <div className="mb-4">
            <label className="block mb-1">Store Code</label>
            <input
              type="text"
              className="w-full border px-3 py-2 rounded"
              value={storeCode}
              onChange={(e) => setStoreCode(e.target.value)}
              required
            />
          </div>
          <div className="mb-4">
            <label className="block mb-1">Branch Name</label>
            <input
              type="text"
              className="w-full border px-3 py-2 rounded"
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              required
            />
          </div>
          <div className="mb-4">
            <label className="block mb-1">Assign Menu Item</label>
            <select
              className="w-full border px-3 py-2 rounded"
              value={menuItemId}
              onChange={(e) => setMenuItemId(e.target.value)}
            >
              <option value="">-- Select Menu Item --</option>
              {menuItems.map(item => (
                <option key={item.id} value={item.id}>
                  {item.title}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-4">
            <label className="block mb-1">Screen Position</label>
            <select
              className="w-full border px-3 py-2 rounded"
              value={screenPosition}
              onChange={(e) => setScreenPosition(e.target.value)}
            >
              <option value="">-- Select Screen Position --</option>
              <option value="Screen 1">Screen 1</option>
              <option value="Screen 2">Screen 2</option>
              <option value="Screen 3">Screen 3</option>
            </select>
          </div>
          <div className="mb-4 flex items-center justify-between rounded border px-3 py-2">
            <span className="font-medium text-gray-700">Show AQI</span>
            <button
              type="button"
              role="switch"
              aria-checked={showAQI}
              onClick={() => setShowAQI((value) => !value)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                showAQI ? 'bg-teal-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                  showAQI ? 'translate-x-5' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded border"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-teal-500 text-white px-4 py-2 rounded"
            >
              {currentDevice ? 'Update' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddDevice;
